import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { api, smpwPath } from "./api";
import QueryKeys from "./queryKeys";
import {
  AddRemoveUserAvailabilities,
  AddRemoveZoneAssignments,
  hgdbSMPWUserPermission,
  hgdbSMPWUserZoneAssignment,
  hgdbSMPWZone,
  hgdbSMPWZoneSetting,
  PresignedPost,
  SMPWAddRemovePairings,
  SMPWAutoFillRequestStatus,
  SMPWEmailRequest,
  SMPWInvite,
  SMPWLocation,
  SMPWMy,
  SMPWNewCongInput,
  SMPWPairing,
  SMPWRegisterNewZone,
  SMPWSchedule,
  SMPWUser,
  SMPWUserAvailability,
  SMPWZone,
  SMPWZoneDefault,
  SMPWZoneSetting,
  ZoneGeo,
} from "../types/smpw";
import { nameOfUser } from "../helpers/user";
import ky from "ky";
import { Congregation } from "../types/scheduling/weekend";
import { InviteResponse } from "../types/invite";
import { RegistrationModel } from "../types/register/registerModel";

export class SMPWAPI {
  base = smpwPath("");

  private smpwScheduleExtendedOptions = {
    parseJson: (data: string) => JSON.parse(data, (key, value) => (key === "datetime" ? new Date(value) : value)),
  };

  async regZone(model: SMPWRegisterNewZone): Promise<void> {
    const url = `${this.base}/regzone`;
    return api.post(url, { json: model }).json();
  }

  async regUser(model: RegistrationModel): Promise<ZoneGeo> {
    const url = `${this.base}/reg/user`;
    return api.post(url, { json: model }).json();
  }

  async getGeoInfo(lat: number, lng: number): Promise<ZoneGeo> {
    const url = `${this.base}/reg`;
    return api.put(url, { json: { lat: lat, lng: lng } }).json();
  }

  async getZoneDefaults(): Promise<SMPWZoneDefault[]> {
    const url = `${this.base}/zones/defaults`;
    return api.get(url).json();
  }

  async setZoneDefault(input: SMPWZoneDefault): Promise<SMPWZoneDefault> {
    const url = `${this.base}/zones/defaults`;
    return api.put(url, { json: input }).json();
  }

  async deleteZoneDefault(input: SMPWZoneDefault): Promise<void> {
    const url = `${this.base}/zones/defaults`;
    return api.delete(url, { json: input }).json();
  }

  async getZones(): Promise<SMPWZone> {
    const url = `${this.base}/zones`;
    return api.get(url).json();
  }

  async setZone(input: hgdbSMPWZone): Promise<SMPWZone> {
    const url = `${this.base}/zones`;
    return api.put(url, { json: input }).json();
  }

  async deleteZone(input: hgdbSMPWZone): Promise<hgdbSMPWZone> {
    const url = `${this.base}/zones`;
    return api.delete(url, { json: input }).json();
  }

  static userFromResponse(user: SMPWUser, resetLastUpdated = false): SMPWUser {
    user.displayName = nameOfUser(user);
    if (resetLastUpdated) user.last_updated = null;
    if (!user.per_day) user.per_day = 1;
    if (!user.per_week) user.per_week = 1;
    if (!user.per_month) user.per_month = 1;
    return user;
  }

  async getUsers(): Promise<SMPWUser[]> {
    const url = `${this.base}/users`;
    const data: SMPWUser[] = await api.get(url).json();
    return await Promise.all(data.map((u: SMPWUser) => SMPWAPI.userFromResponse(u, true)));
  }

  async setUser(user: SMPWUser): Promise<SMPWUser> {
    const url = `${this.base}/users`;
    return api.put(url, { json: user }).json();
  }

  async deleteUser(user: SMPWUser): Promise<SMPWUser> {
    const url = `${this.base}/users`;
    return api.delete(url, { json: user }).json();
  }

  async seachUserEmail(email: string): Promise<SMPWUser> {
    const url = `${this.base}/search/users`;
    const u: SMPWUser = await api.post(url, { json: { email: email } }).json();

    return SMPWAPI.userFromResponse(u, true);
  }

  async getPermissions(): Promise<hgdbSMPWUserPermission[]> {
    const url = `${this.base}/permissions`;
    return api.get(url).json();
  }

  async setPermissions(input: hgdbSMPWUserPermission[]): Promise<hgdbSMPWUserPermission[]> {
    const url = `${this.base}/permissions`;
    return api.put(url, { json: input }).json();
  }

  async getLocations(): Promise<SMPWLocation[]> {
    const url = `${this.base}/locations`;
    return api.get(url).json();
  }

  async setLocation(input: SMPWLocation): Promise<SMPWLocation> {
    const url = `${this.base}/locations`;
    return api.put(url, { json: input }).json();
  }

  async deleteLocation(input: SMPWLocation): Promise<SMPWLocation> {
    const url = `${this.base}/locations`;
    return api.delete(url, { json: input }).json();
  }

  async getAssignments(): Promise<hgdbSMPWUserZoneAssignment[]> {
    const url = `${this.base}/assignments`;
    return api.get(url).json();
  }

  async setAssignments(input: AddRemoveZoneAssignments): Promise<hgdbSMPWUserZoneAssignment[]> {
    const url = `${this.base}/assignments`;
    return api.put(url, { json: input }).json();
  }

  async getUserSchedules(userID: number): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { userID: userID } })
      .json();
  }

  async getLocationSchedules(locID: number, from: string, to: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/loc`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { locID: locID, from: from, to: to } })
      .json();
  }

  async getZoneSchedules(zoneID: number, from: string, to: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/zone`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { zoneID: zoneID, from: from, to: to } })
      .json();
  }

  async generateSchedules(zone_id: number, month: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/generate`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .post(url, { json: { zone_id: zone_id, month: month } })
      .json();
  }

  async autofillSchedules(zone_id: number, month: string): Promise<void> {
    const url = `${this.base}/schedules/autofill`;
    api.post(url, { json: { zone_id: zone_id, month: month } });
    return;
  }

  async getAutoFillRequestStatus(zone_id: number, month: string): Promise<SMPWAutoFillRequestStatus> {
    const url = `${this.base}/schedules/autofill/status`;
    return api.get(url, { searchParams: { zone_id: zone_id, month: month } }).json();
  }

  async clearSchedules(zone_id: number, from: string, to: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/range`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .delete(url, { json: { id: zone_id, from: from, to: to } })
      .json();
  }

  async setSchedules(input: SMPWSchedule[]): Promise<void> {
    const url = `${this.base}/schedules`;
    return api.put(url, { json: input }).json();
  }

  async getAvailabilities(): Promise<SMPWUserAvailability[]> {
    const url = `${this.base}/availabilities`;
    return api.get(url).json();
  }

  async setAvailabilities(input: AddRemoveUserAvailabilities): Promise<void> {
    const url = `${this.base}/availabilities`;
    return api.put(url, { json: input }).json();
  }

  async getZoneSettings(zone_id: number): Promise<SMPWZoneSetting> {
    const url = `${this.base}/settings/${zone_id}`;
    return api.get(url).json();
  }

  async getAllZonesSettings(): Promise<SMPWZoneSetting[]> {
    const url = `${this.base}/settings`;
    return api.get(url).json();
  }

  async setSettings(input: hgdbSMPWZoneSetting): Promise<void> {
    const url = `${this.base}/settings`;
    return api.put(url, { json: input }).json();
  }

  async getPairings(): Promise<SMPWPairing[]> {
    const url = `${this.base}/pairings`;
    return api.get(url).json();
  }

  async setPairings(input: SMPWAddRemovePairings): Promise<void> {
    const url = `${this.base}/pairings`;
    return api.put(url, { json: input }).json();
  }

  async getMy(): Promise<SMPWMy> {
    const url = `${this.base}/my`;
    return api.get(url).json();
  }

  async getNearbyCongregations(): Promise<Congregation[]> {
    const url = `${this.base}/congs`;
    return api.get(url).json();
  }

  async addCongregations(input: SMPWNewCongInput, forReg = false): Promise<Congregation> {
    const url = forReg ? `${this.base}/reg/congs` : `${this.base}/congs`;
    return api.put(url, { json: input }).json();
  }

  // getInvite fetches a new invite link
  async getInvite(user_id: number): Promise<InviteResponse> {
    const url = `fsreport/invite/${user_id}`;
    return api.put(url, { json: { inviteType: "email" } }).json();
  }

  // getMySMPWInvites gets invitations from SMPW Zones
  async getMySMPWInvites(): Promise<SMPWInvite[]> {
    const url = `${this.base}/invite/my`;
    return api.get(url).json();
  }

  // getSMPWInvites get all invitations for an SMPW Zones
  async getSMPWInvites(): Promise<SMPWInvite[]> {
    const url = `${this.base}/invite/zone`;
    return api.get(url).json();
  }

  // createSMPWInvite creates an invitation for a user to an SMPW Zones
  async createSMPWInvite(user: SMPWUser): Promise<SMPWInvite> {
    const url = `${this.base}/invite`;
    return api.put(url, { json: user }).json();
  }

  async deleteSMPWInvite(invite: SMPWInvite): Promise<void> {
    const url = `${this.base}/invite`;
    return api.delete(url, { json: invite }).json();
  }

  async respondToMyInvite(invite: SMPWInvite): Promise<void> {
    const url = `${this.base}/invite`;
    return api.post(url, { json: invite }).json();
  }

  async sendBulkEmail(input: SMPWEmailRequest): Promise<void> {
    const url = `${this.base}/email`;
    return api.post(url, { json: input }).json();
  }

  private async getPresignedURL(id: number, type: "user" | "location"): Promise<PresignedPost> {
    const url = `${this.base}/images/presigned`;
    let payload;
    switch (type) {
      case "user":
        payload = { user_id: id };
        break;
      case "location":
        payload = { location_id: id };
    }

    return api.post(url, { json: payload }).json();
  }

  async uploadImage(id: number, file: File, type: "user" | "location"): Promise<string | undefined> {
    const psp = await this.getPresignedURL(id, type);
    const url = psp.URL;

    const formData = new FormData();
    Object.entries(psp.Values).forEach(([k, v]) => {
      formData.append(k, v);
    });
    formData.append("file", file);

    const key = psp.Values["key"];
    const extApi = ky.create();
    await extApi.post(url, { body: formData }).json();
    return key;
  }
}

export function useSMPWMyInvites(options?: UseQueryOptions<SMPWInvite[], Error>) {
  return useQuery<SMPWInvite[], Error>({
    queryKey: [QueryKeys.MySMPWInvites],
    queryFn: () => smpwAPI.getMySMPWInvites(),
    ...options,
  });
}

export function useSMPWZoneInvites(options?: UseQueryOptions<SMPWInvite[], Error>) {
  return useQuery<SMPWInvite[], Error>({
    queryKey: [QueryKeys.SMPWZoneInvites],
    queryFn: () => smpwAPI.getSMPWInvites(),
    ...options,
  });
}

export function useSMPWMy(options?: UseQueryOptions<SMPWMy, Error>) {
  return useQuery<SMPWMy, Error>({
    queryKey: [QueryKeys.SMPWMy],
    queryFn: () => smpwAPI.getMy(),
    ...options,
  });
}

export function useSMPWZones(options?: UseQueryOptions<SMPWZone, Error>) {
  return useQuery<SMPWZone, Error>({
    queryKey: [QueryKeys.SMPWZone],
    queryFn: () => smpwAPI.getZones(),
    ...options,
  });
}

export function useSMPWZoneDefaults(options?: UseQueryOptions<SMPWZoneDefault[], Error>) {
  return useQuery<SMPWZoneDefault[], Error>({
    queryKey: [QueryKeys.SMPWZoneDefaults],
    queryFn: () => smpwAPI.getZoneDefaults(),
    ...options,
  });
}

export function useSMPWUsers(options?: UseQueryOptions<SMPWUser[], Error>) {
  return useQuery<SMPWUser[], Error>({
    queryKey: [QueryKeys.SMPWUsers],
    queryFn: () => smpwAPI.getUsers(),
    ...options,
  });
}

export function useSMPWAssignments(options?: UseQueryOptions<hgdbSMPWUserZoneAssignment[], Error>) {
  return useQuery<hgdbSMPWUserZoneAssignment[], Error>({
    queryKey: [QueryKeys.SMPWUserZoneAssignment],
    queryFn: () => smpwAPI.getAssignments(),
    ...options,
  });
}

export function useSMPWPermissions(options?: UseQueryOptions<hgdbSMPWUserPermission[], Error>) {
  return useQuery<hgdbSMPWUserPermission[], Error>({
    queryKey: [QueryKeys.SMPWUserPermissions],
    queryFn: () => smpwAPI.getPermissions(),
    ...options,
  });
}

export function useSMPWLocations(options?: UseQueryOptions<SMPWLocation[], Error>) {
  return useQuery<SMPWLocation[], Error>({
    queryKey: [QueryKeys.SMPWLocations],
    queryFn: () => smpwAPI.getLocations(),
    ...options,
  });
}

export function useSMPWAvailabilities(options?: UseQueryOptions<SMPWUserAvailability[], Error>) {
  return useQuery<SMPWUserAvailability[], Error>({
    queryKey: [QueryKeys.SMPWUserAvailabilities],
    queryFn: () => smpwAPI.getAvailabilities(),
    ...options,
  });
}

export function useSMPWPairings(options?: UseQueryOptions<SMPWPairing[], Error>) {
  return useQuery<SMPWPairing[], Error>({
    queryKey: [QueryKeys.SMPWPairings],
    queryFn: () => smpwAPI.getPairings(),
    ...options,
  });
}

export function useSMPWZoneSettings(options?: UseQueryOptions<SMPWZoneSetting[], Error>) {
  return useQuery<SMPWZoneSetting[], Error>({
    queryKey: [QueryKeys.SMPWZoneSetting],
    queryFn: () => smpwAPI.getAllZonesSettings(),
    ...options,
  });
}

export function useSMPWUserSchedules(user_id: number, options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWUserSchedules, user_id],
    queryFn: () => smpwAPI.getUserSchedules(user_id),
    ...options,
  });
}

export function useSMPWLocationSchedules(
  loc_id: number,
  from: string,
  to: string,
  options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>,
) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWLocSchedules, loc_id, from, to],
    queryFn: () => smpwAPI.getLocationSchedules(loc_id, from, to),
    ...options,
  });
}

export function useSMPWZoneSchedules(
  zone_id: number,
  from: string,
  to: string,
  options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>,
) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWZoneSchedules, zone_id, from, to],
    queryFn: () => smpwAPI.getZoneSchedules(zone_id, from, to),
    ...options,
  });
}

export function useNearbyCongregations(options?: Partial<UseQueryOptions<Congregation[], Error>>) {
  return useQuery<Congregation[], Error>({
    queryKey: [QueryKeys.SMPWNearbyCongregations],
    queryFn: () => smpwAPI.getNearbyCongregations(),
    ...options,
  });
}

export function useAutoFillRequestStatus(
  zone_id: number,
  month: string,
  options?: Partial<UseQueryOptions<SMPWAutoFillRequestStatus, Error>>,
) {
  return useQuery<SMPWAutoFillRequestStatus, Error>({
    queryKey: [QueryKeys.AutoFillRequestStatus],
    queryFn: () => smpwAPI.getAutoFillRequestStatus(zone_id, month),
    ...options,
  });
}

export const smpwAPI = new SMPWAPI();
